import React, { useState, useEffect, useContext } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Typography,
  Divider,
  Avatar,
  Chip,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AuthContext } from '../../context/AuthContext';

// NOTA: Este arquivo foi completamente reescrito para corrigir a estrutura do modal
// O problema anterior era que o clique no Box fechava o modal
// Agora o checkbox é o único elemento interativo dentro do Box

export default function CourseEnrollmentModal({
  open,
  onClose,
  userId,
  userName,
  onEnrollmentSuccess,
}) {
  const { user: currentUser } = useContext(AuthContext);
  const [courses, setCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Busca os cursos disponíveis baseado no role do usuário
  useEffect(() => {
    if (!open || !userId) return;

    const fetchAvailableCourses = async () => {
      setLoading(true);
      setError(null);
      try {
        // Se o usuário atual é ADMIN_CLIENT, busca apenas cursos em que está matriculado
        if (currentUser?.role === 'ADMIN_CLIENT') {
          const enrollmentsRes = await api.get('/enrollments');
          const userEnrollments = enrollmentsRes.data.filter(
            e => e.companyId === currentUser.companyId
          );
          const courseIds = [...new Set(userEnrollments.map(e => e.courseId))];

          // Busca os detalhes dos cursos
          const coursesData = await Promise.all(
            courseIds.map(courseId =>
              api
                .get(`/courses/${courseId}`)
                .then(res => res.data)
                .catch(() => null)
            )
          );

          setCourses(coursesData.filter(c => c !== null));
        } else {
          // ADMIN_EAD e INSTRUCTOR veem todos os cursos que eles têm acesso
          const res = await api.get('/courses');
          setCourses(res.data || []);
        }

        setSelectedCourses([]);
      } catch (err) {
        console.error('Erro ao buscar cursos:', err);
        setError('Erro ao carregar cursos disponíveis. Tente novamente.');
        toast.error('Erro ao carregar cursos');
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableCourses();
  }, [open, userId, currentUser?.role, currentUser?.companyId]);

  const handleCourseToggle = courseId => {
    setSelectedCourses(prev => {
      if (prev.includes(courseId)) {
        return prev.filter(id => id !== courseId);
      }
      return [...prev, courseId];
    });
  };

  const handleSelectAll = () => {
    if (selectedCourses.length === courses.length) {
      setSelectedCourses([]);
    } else {
      setSelectedCourses(courses.map(c => c.id));
    }
  };

  const handleEnroll = async () => {
    if (selectedCourses.length === 0) {
      toast.error('Selecione pelo menos um curso');
      return;
    }

    setSubmitting(true);
    try {
      // Matricula o usuário em todos os cursos selecionados
      await Promise.all(
        selectedCourses.map(courseId =>
          api.post(`/enrollments/course/${courseId}/enroll-user`, { userId })
        )
      );

      toast.success(`Usuário matriculado em ${selectedCourses.length} curso(s)`);

      if (onEnrollmentSuccess) {
        onEnrollmentSuccess();
      }

      handleClose();
    } catch (err) {
      console.error('Erro ao matricular usuário:', err);
      toast.error('Erro ao matricular usuário em alguns cursos');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedCourses([]);
    setError(null);
    onClose();
  };

  let selectAllLabel;
  if (selectedCourses.length === courses.length && courses.length > 0) {
    selectAllLabel = `Desselecionar todos (${courses.length} selecionados)`;
  } else if (selectedCourses.length > 0) {
    selectAllLabel = `${selectedCourses.length} de ${courses.length} selecionados`;
  } else {
    selectAllLabel = `Selecionar todos (${courses.length})`;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      hideBackdrop={true}
      disableScrollLock={true}
      onClick={e => e.stopPropagation()}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <SchoolIcon />
        Matricular em Cursos
      </DialogTitle>

      <DialogContent
        sx={{ p: 3, background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)' }}
        onClick={e => e.stopPropagation()}
      >
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: '12px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#fca5a5',
            }}
          >
            {error}
          </Alert>
        )}

        {/* Info sobre o usuário */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: '#2d3748',
            borderRadius: '12px',
            border: '1px solid rgba(102, 126, 234, 0.3)',
          }}
        >
          <Typography variant="body2" sx={{ color: '#cbd5e0', mb: 1 }}>
            Matriculando usuário:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              {userName?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#e2e8f0' }}>
                {userName}
              </Typography>
              <Typography variant="caption" sx={{ color: '#a0aec0' }}>
                ID: {userId}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        {/* Info sobre os cursos disponíveis */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#cbd5e0', fontWeight: 600, mb: 1 }}>
            {currentUser?.role === 'ADMIN_CLIENT'
              ? `${courses.length} curso(s) em que você está matriculado:`
              : `${courses.length} curso(s) disponível(is):`}
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && courses.length === 0 && (
          <Alert
            severity="info"
            sx={{
              borderRadius: '12px',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              color: '#93c5fd',
            }}
          >
            Nenhum curso disponível no momento
          </Alert>
        )}

        {!loading && courses.length > 0 && (
          <>
            {/* Selecionar todos */}
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#2d3748',
                borderRadius: '12px',
                border: '1px solid rgba(102, 126, 234, 0.3)',
              }}
              onClick={e => e.stopPropagation()}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedCourses.length === courses.length && courses.length > 0}
                    indeterminate={
                      selectedCourses.length > 0 && selectedCourses.length < courses.length
                    }
                    onChange={handleSelectAll}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 600, color: '#667eea' }}>
                    {selectAllLabel}
                  </Typography>
                }
              />
            </Box>

            {/* Lista de cursos */}
            <FormGroup sx={{ gap: 1.5, maxHeight: '400px', overflowY: 'auto' }}>
              {courses.map(course => (
                <Box
                  key={course.id}
                  onClick={e => e.stopPropagation()}
                  sx={{
                    p: 2,
                    backgroundColor: '#2d3748',
                    borderRadius: '12px',
                    border: '2px solid',
                    borderColor: selectedCourses.includes(course.id)
                      ? '#667eea'
                      : 'rgba(102, 126, 234, 0.1)',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#667eea',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Checkbox
                      checked={selectedCourses.includes(course.id)}
                      onChange={() => handleCourseToggle(course.id)}
                      sx={{ mt: 0.5 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: '#e2e8f0', flex: 1 }}
                        >
                          {course.title}
                        </Typography>
                        {selectedCourses.includes(course.id) && (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Selecionado"
                            size="small"
                            sx={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                      {course.description && (
                        <Typography variant="body2" sx={{ color: '#cbd5e0', mb: 1 }}>
                          {course.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        {course.code && (
                          <Chip
                            label={`Código: ${course.code}`}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: '#4a5568', color: '#cbd5e0' }}
                          />
                        )}
                        {course.level && (
                          <Chip
                            label={course.level}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: '#4a5568', color: '#cbd5e0' }}
                          />
                        )}
                        {course.status && (
                          <Chip
                            label={course.status === 'PUBLISHED' ? 'Publicado' : 'Rascunho'}
                            size="small"
                            sx={{
                              background:
                                course.status === 'PUBLISHED'
                                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                  : 'rgba(74, 85, 104, 0.5)',
                              color: course.status === 'PUBLISHED' ? 'white' : '#cbd5e0',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </FormGroup>
          </>
        )}

        {/* Resumo de seleção */}
        {selectedCourses.length > 0 && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: 'rgba(102, 126, 234, 0.15)',
              borderRadius: '12px',
              border: '1px solid rgba(102, 126, 234, 0.3)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#a5b4fc', mb: 1 }}>
              ✓ {selectedCourses.length} curso(s) selecionado(s)
            </Typography>
            <Typography variant="caption" sx={{ color: '#cbd5e0' }}>
              Ao clicar em &quot;Matricular&quot;, o usuário será adicionado a todos os cursos
              selecionados acima.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ p: 3, gap: 1, background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)' }}
      >
        <Button onClick={handleClose} sx={{ color: '#a0aec0', fontWeight: 600 }}>
          Cancelar
        </Button>
        <Button
          onClick={handleEnroll}
          disabled={submitting || selectedCourses.length === 0}
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '8px',
            fontWeight: 600,
            px: 3,
          }}
        >
          {submitting ? 'Matriculando...' : `Matricular em ${selectedCourses.length} Curso(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
